/* eslint-disable jsx-a11y/anchor-is-valid */
import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import EventsService from "../services/EventsService";

const actionsColorMap = [
  "#1E8E3A", "#0A84FF", "#C62828", "#B8960A",
  "#8E44AD", "#E67E22", "#00BCD4", "#E91E63", "#3F51B5", "#7CB342",
];

const CATEGORY_OPTIONS = [
  { value: "", label: "All Categories" },
  { value: "work", label: "Work" },
  { value: "health", label: "Health" },
  { value: "leisure", label: "Leisure" },
  { value: "family", label: "Family" },
  { value: "self-care", label: "Self-Care" },
  { value: "chores", label: "Chores" },
  { value: "other", label: "Other" },
];

function MyEventsPage() {
  const [events, setEvents] = useState([]);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("");
  const [tagFilter, setTagFilter] = useState("");
  const navigate = useNavigate();

  const fetchEvents = useCallback(async () => {
    try {
      setLoading(true);
      const hasFilters = searchQuery.trim() || categoryFilter || tagFilter.trim();
      let data;
      if (hasFilters) {
        try {
          data = await EventsService.searchEvents({
            query: searchQuery.trim() || undefined,
            category: categoryFilter || undefined,
            tag: tagFilter.trim() || undefined,
          });
        } catch {
          data = await EventsService.getAllEvents();
        }
      } else {
        data = await EventsService.getAllEvents();
      }
      setEvents(data || []);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [searchQuery, categoryFilter, tagFilter]);

  useEffect(() => {
    const debounce = setTimeout(() => {
      fetchEvents();
    }, 300);
    return () => clearTimeout(debounce);
  }, [fetchEvents]);

  const handleAction = async (action, eventId) => {
    try {
      if (action === "duplicate") {
        await EventsService.duplicateEvent(eventId);
      } else if (action === "delete") {
        await EventsService.deleteEvent(eventId);
      }
      fetchEvents();
    } catch (err) {
      alert(err.message);
    }
  };

  const handleExport = async (eventId) => {
    try {
      const data = await EventsService.exportEvent(eventId);
      const url = window.URL.createObjectURL(new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' }));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `event_${eventId}.json`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (err) {
      alert(err.message);
    }
  };

  const handleEventClick = (event) => {
    if (event.started) {
      navigate(`/event/${event.id}`);
    } else {
      navigate(`/event/${event.id}/step2`);
    }
  };

  const filteredEvents = events.filter((event) => {
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      const nameMatch = event.name?.toLowerCase().includes(q);
      const tagMatch = event.tags?.some((t) => t.toLowerCase().includes(q));
      if (!nameMatch && !tagMatch) return false;
    }
    if (categoryFilter && event.category !== categoryFilter) return false;
    if (tagFilter.trim()) {
      const tf = tagFilter.toLowerCase();
      if (!event.tags?.some((t) => t.toLowerCase().includes(tf))) return false;
    }
    return true;
  });

  return (
    <>
      <div className="page-header">
        <nav className="navbar navbar-expand-lg d-flex justify-content-between">
          <div className="header-title flex-fill">
            <a
              id="sidebar-toggle"
              onClick={(e) => {
                e.preventDefault();
                window.__toggleSidebar && window.__toggleSidebar(e);
              }}
            >
              <i className="fas fa-bars"></i>
            </a>
            <h5>My Activities</h5>
          </div>
        </nav>
      </div>
      <div className="main-wrapper">
        {/* Search & Filters */}
        <div className="glass-card" style={{ padding: 16, marginBottom: 20 }}>
          <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
            <div style={{ flex: "2 1 200px", position: "relative" }}>
              <i className="fas fa-search" style={{ position: "absolute", left: 12, top: "50%", transform: "translateY(-50%)", color: "rgba(255,255,255,0.3)", fontSize: 13 }}></i>
              <input
                type="text"
                className="form-control"
                placeholder="Search activities..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                style={{ paddingLeft: 36 }}
              />
            </div>
            <select
              className="form-control"
              value={categoryFilter}
              onChange={(e) => setCategoryFilter(e.target.value)}
              style={{ flex: "1 1 150px", maxWidth: 200 }}
            >
              {CATEGORY_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
            <input
              type="text"
              className="form-control"
              placeholder="Filter by tag..."
              value={tagFilter}
              onChange={(e) => setTagFilter(e.target.value)}
              style={{ flex: "1 1 140px", maxWidth: 180 }}
            />
          </div>
        </div>

        {loading && (
          <div className="d-flex justify-content-center" style={{ paddingTop: 60 }}>
            <div className="spinner-border" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
          </div>
        )}
        {error && <p className="text-danger">{error}</p>}
        {!loading && filteredEvents.length === 0 && (
          <div style={{ textAlign: 'center', padding: '60px 20px' }}>
            <i className="far fa-calendar-alt" style={{ fontSize: 48, color: 'rgba(255,255,255,0.15)', marginBottom: 16, display: 'block' }}></i>
            <p style={{ color: 'rgba(255,255,255,0.4)', fontSize: 16 }}>
              {events.length === 0
                ? "No activities yet. Create your first activity to get started."
                : "No activities match your filters."}
            </p>
            {events.length === 0 && (
              <button
                className="btn btn-info"
                style={{ marginTop: 12, padding: '10px 28px' }}
                onClick={() => navigate('/create-event')}
              >
                <i className="fas fa-plus" style={{ marginRight: 8 }}></i>
                Create Activity
              </button>
            )}
          </div>
        )}

        <div className="events-grid">
          {filteredEvents.map((event) => (
            <div
              key={event.id}
              className="card event-card"
              onClick={() => handleEventClick(event)}
              style={{ cursor: "pointer" }}
            >
              <div className="card-body">
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
                  <h5 className="card-title" style={{ margin: 0, fontSize: 18 }}>
                    {event.name}
                  </h5>
                  <span
                    className={`badge ${
                      event.started && !event.finished
                        ? "bg-success"
                        : event.finished
                        ? "bg-warning"
                        : "bg-warning"
                    }`}
                  >
                    {event.started && !event.finished
                      ? "Active"
                      : event.finished
                      ? "Finished"
                      : "Draft"}
                  </span>
                </div>

                {/* Category & Tags */}
                <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 10 }}>
                  {event.category && (
                    <span className="category-chip">
                      {event.category}
                    </span>
                  )}
                  {event.tags?.map((tag, i) => (
                    <span key={i} className="tag-chip tag-chip-small">
                      {tag}
                    </span>
                  ))}
                </div>

                {event.actions && event.actions.length > 0 && (
                  <div style={{ display: 'flex', gap: 6, marginBottom: 12 }}>
                    {event.actions.map((a, i) => (
                      <span
                        key={i}
                        style={{
                          width: 8,
                          height: 8,
                          borderRadius: '50%',
                          backgroundColor: actionsColorMap[i % actionsColorMap.length],
                          display: 'inline-block',
                        }}
                      />
                    ))}
                  </div>
                )}

                <div className="btn-group" onClick={(e) => e.stopPropagation()}>
                  <button
                    className="btn btn-sm btn-primary"
                    onClick={() => handleAction("duplicate", event.id)}
                  >
                    <i className="fas fa-copy" style={{ marginRight: 4 }}></i>
                    Duplicate
                  </button>
                  <button
                    className="btn btn-sm btn-danger"
                    onClick={() => handleAction("delete", event.id)}
                  >
                    <i className="fas fa-trash" style={{ marginRight: 4 }}></i>
                    Delete
                  </button>
                  <button
                    className="btn btn-sm btn-primary"
                    onClick={() => handleExport(event.id)}
                  >
                    <i className="fas fa-download" style={{ marginRight: 4 }}></i>
                    Export
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </>
  );
}

export default MyEventsPage;
